import * as React from 'react';
import { BaseButton } from '../base/button';
import { DEFAULT_ACCEPTED_FILE_TYPES, MAX_BATCH_UPLOAD_FILES } from './types';
import { formatFileSize } from './upload-utils';
import * as S from './batch-file-uploader.styles';

export interface BatchFileUploaderProps {
    files: File[];
    onChange: (files: File[]) => void;
    accept?: string;
    maxFiles?: number;
    disabled?: boolean;
    errorMessage?: string;
}

export const BatchFileUploader: React.FC<BatchFileUploaderProps> = ({
    files,
    onChange,
    accept = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFiles = MAX_BATCH_UPLOAD_FILES,
    disabled = false,
    errorMessage,
}) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [selectionError, setSelectionError] = React.useState<string | undefined>();

    const handleChooseClick = (): void => {
        inputRef.current?.click();
    };

    const handleAddFiles = (selectedFiles: FileList | null): void => {
        if (!selectedFiles || selectedFiles.length === 0) {
            return;
        }

        const incomingFiles = Array.from(selectedFiles);
        const availableSlots = maxFiles - files.length;

        if (availableSlots <= 0) {
            setSelectionError(`You can upload up to ${maxFiles} files.`);
            return;
        }

        const filesToAdd = incomingFiles.slice(0, availableSlots);
        const skippedCount = incomingFiles.length - filesToAdd.length;

        if (skippedCount > 0) {
            setSelectionError(`Only ${filesToAdd.length} file(s) were added. Maximum is ${maxFiles}.`);
        } else {
            setSelectionError(undefined);
        }

        onChange([...files, ...filesToAdd]);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        handleAddFiles(event.target.files);
        event.target.value = '';
    };

    const handleRemoveFile = (index: number): void => {
        onChange(files.filter((_, fileIndex) => fileIndex !== index));
        setSelectionError(undefined);
    };

    const handleClearAll = (): void => {
        onChange([]);
        setSelectionError(undefined);
    };

    const displayError = errorMessage || selectionError;
    const canAddMore = files.length < maxFiles;

    return (
        <S.Container>
            <S.FileActions>
                <BaseButton type="button" variant="primary" onClick={handleChooseClick} disabled={disabled || !canAddMore}>
                    {files.length > 0 ? 'Add Files' : 'Choose Files'}
                </BaseButton>
                {files.length > 0 && (
                    <BaseButton type="button" variant="warning" onClick={handleClearAll} disabled={disabled}>
                        Clear All
                    </BaseButton>
                )}
                <S.Summary>
                    {files.length} / {maxFiles} files selected
                </S.Summary>
            </S.FileActions>

            <S.HiddenInput
                ref={inputRef}
                type="file"
                accept={accept}
                multiple
                onChange={handleInputChange}
                disabled={disabled || !canAddMore}
            />

            {files.length > 0 && (
                <S.FileList>
                    {files.map((file, index) => (
                        <S.FileRow key={`${file.name}-${file.size}-${file.lastModified}-${index}`}>
                            <S.FileInfo>
                                <S.FileName title={file.name}>{file.name}</S.FileName>
                                <S.FileSize>{formatFileSize(file.size)}</S.FileSize>
                            </S.FileInfo>
                            <S.RemoveButton
                                type="button"
                                variant="warning"
                                onClick={() => handleRemoveFile(index)}
                                disabled={disabled}>
                                Remove
                            </S.RemoveButton>
                        </S.FileRow>
                    ))}
                </S.FileList>
            )}

            {displayError && <S.ErrorMessage>{displayError}</S.ErrorMessage>}
        </S.Container>
    );
};
